import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { CreateBlockProgressDocument, LoadContentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { FeatureWall } from 'modules/core/components/FeatureWall'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

export default observer(() => {
  const { trainingId } = useParams()
  const { employee: currentEmployee } = useCurrentSession()

  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
      employeeId: currentEmployee.id,
    },
  })

  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return null
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(trainingId))
  }, [trainingId, data, loading])

  const [createBlockProgress] = useMutation(CreateBlockProgressDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.createTrainingsContentBlockProgress
    },
  })

  if (!collection || loading) {
    return <Loading expand />
  }

  // Create an async function so the button shows a loading state
  const handleNext = async () => {
    if (collection.selectedItem instanceof Block) {
      const currentBlock = collection.selectedItem

      // TODO: Check block completed before creating the progress in the
      //  backend (for example video and quiz)

      await createBlockProgress({
        variables: {
          contentBlockId: currentBlock.id,
          employeeId: currentEmployee.id,
        },
      })
        .then((result) => {
          const handled = handleResult(result, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('common.error'),
            })
          })
          if (handled.success) {
            // If we enter here, the progress exists in backend, so we
            // mark the current and next block as accesible
            currentBlock.accesible = true

            //<editor-fold desc="Set accesible next item on sidebar">
            const items = collection.flatItems
            const currentIndex = items.findIndex((item) => item.uuid === currentBlock.uuid)
            if (currentIndex != -1 && currentIndex < items.length - 1) {
              // Get the block after current
              let nextItem = items[currentIndex + 1]
              // if current block is last of module, next item will be the next module
              if (nextItem instanceof ContentModule && currentIndex < items.length - 2) {
                // get the next item, that will be the first block of the new module
                nextItem = items[currentIndex + 2]
              }
              if (nextItem instanceof Block) {
                nextItem.accesible = true
              }
            }
            //</editor-fold>

            collection.next()
          }
        })
        .catch((_error) => {
          // Mutation has failed
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        })
    } else if (collection.selectedItem instanceof ContentModule) {
      collection.next()
    }
  }

  if (!collection.selectedItem) return null

  return (
    <FeatureWall featureName="dev_training_lms">
      <ResponsivePage.Body variant="fullscreen">
        <ContentConsumer
          tocItems={collection.modules.map((contentModule) => ({
            id: contentModule.uuid,
            label: contentModule.title,
            onClick: () => collection.setSelected(contentModule.uuid),
            children: contentModule.blocks.map((block) => ({
              id: block.uuid,
              label: block.title,
              onClick: () => collection.setSelected(block.uuid),
              disabled: !block.accesible,
            })),
            //module is accesible if it has accesible blocks
            disabled: !contentModule.blocks.some((block) => block.accesible),
          }))}
          selectedItem={collection.selectedItem}
        />
        <Divider />
        <Box
          flexDirection="row"
          justifyContent="flexEnd"
          gap="s16"
          padding="s16"
          width="full"
          paddingRight="s64"
        >
          {/* TODO: Add calculated percentage here */}
          <Box gap="s16" flexDirection="row">
            <Button
              label={i18n.t('trainings.content.consumer.back')}
              variant="secondary"
              onClick={() => {
                collection.back()
              }}
            />
            <Button
              label={i18n.t('trainings.content.consumer.continue')}
              disabled={false}
              onClick={handleNext}
            />{' '}
            {/* Disable based on computed completion on the contentItem */}
          </Box>
        </Box>
      </ResponsivePage.Body>
    </FeatureWall>
  )
})
